'use strict';

(() => {
  const root = document.getElementById('journal-discovery');
  const dataNode = document.getElementById('journal-search-data');
  if (!root || !dataNode) return;

  let data;
  try { data = JSON.parse(dataNode.textContent); } catch { return; }
  if (!Array.isArray(data.entries)) return;

  const form = root.querySelector('[data-journal-search-form]');
  const input = root.querySelector('#journal-search');
  const grid = root.querySelector('#journal-results-grid');
  const status = root.querySelector('[data-journal-status]');
  if (!form || !input || !grid || !status) return;

  const cards = Array.from(grid.querySelectorAll('[data-journal-card]'));
  const cardById = new Map(cards.map(card => [card.dataset.journalCard, card]));
  const sourceIds = data.entries.map(entry => entry && entry.id);
  // An incomplete index must leave the complete, usable static article list intact.
  if (!cards.length || cardById.size !== cards.length ||
      sourceIds.length !== cards.length || new Set(sourceIds).size !== cards.length ||
      sourceIds.some(id => !cardById.has(id))) return;

  const normalize = value => String(value || '').normalize('NFKC').toLocaleLowerCase()
    .normalize('NFD').replace(/\p{M}/gu, '').replace(/[’‘]/g, "'");
  const queryValue = value => String(value || '').replace(/\s+/g, ' ').trim().slice(0, 120);
  const tokenize = value => normalize(value).match(/[\p{L}\p{N}]+/gu) || [];
  const parseDate = value => {
    const parsed = Date.parse(value || '');
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const records = data.entries.map((entry, order) => ({
    id: entry.id,
    card: cardById.get(entry.id),
    order,
    title: String(entry.title || ''),
    normalizedTitle: normalize(entry.title),
    normalizedExcerpt: normalize(entry.excerpt),
    text: normalize([entry.title, entry.excerpt, entry.aliases, entry.searchText].join(' ')),
    topics: Array.isArray(entry.topics) ? entry.topics : [],
    date: parseDate(entry.publishedAt || entry.updatedAt),
  }));
  const defaults = {
    results: '{count} articles', oneResult: '1 article',
    showing: 'Showing {shown} of {count}', noResults: 'No articles found',
    queryResults: '{count} articles for “{query}”',
    oneQueryResult: '1 article for “{query}”',
    searching: 'Searching articles…',
  };
  const ui = Object.fromEntries(Object.entries(defaults).map(([key, value]) =>
    [key, typeof data.ui?.[key] === 'string' ? data.ui[key] : value]));
  const format = (template, values) => template.replace(/\{(\w+)\}/g,
    (match, key) => Object.hasOwn(values, key) ? String(values[key]) : match);
  const pageSize = Number.isInteger(data.pageSize) && data.pageSize > 0
    ? Math.min(data.pageSize, 48) : 9;
  const topicButtons = Array.from(root.querySelectorAll('[data-journal-topic]'));
  const allowedTopics = new Set(['all', ...topicButtons.map(button => button.dataset.journalTopic)]);
  const allowedSorts = new Set(['recommended', 'newest', 'az']);
  const sort = root.querySelector('[data-journal-sort]');
  const clear = root.querySelector('[data-journal-clear]');
  const more = root.querySelector('[data-journal-more]');
  const showing = root.querySelector('[data-journal-showing]');
  const empty = root.querySelector('[data-journal-empty]');
  const collator = new Intl.Collator(document.documentElement.lang || undefined,
    { sensitivity: 'base', numeric: true });
  let state = { q: '', topic: 'all', sort: 'recommended', limit: pageSize };
  let timer;
  let lastMatches = [];
  let fullTextUrl = null;
  try {
    if (typeof data.fullTextUrl === 'string') {
      const url = new URL(data.fullTextUrl, window.location.href);
      const trustedOrigin = url.origin === window.location.origin ||
        url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.myshopify.com');
      const expectedAsset = /\/assets\/celluma-revamp-celluma-journal-full-text-[a-z0-9-]+\.json$/.test(url.pathname);
      if (url.protocol === 'https:' && trustedOrigin && expectedAsset && !url.hash &&
          [...url.searchParams.keys()].every(key => key === 'v')) fullTextUrl = url.href;
    }
  } catch { /* Titles, summaries and topic search remain available. */ }
  let fullTextState = fullTextUrl ? 'idle' : 'unavailable';
  let fullTextPromise = null;

  function ensureFullText() {
    if (fullTextPromise) return fullTextPromise;
    if (!fullTextUrl) return Promise.resolve(false);
    fullTextState = 'loading';
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    fullTextPromise = fetch(fullTextUrl, {
      credentials: 'omit', cache: 'force-cache', signal: controller.signal,
    }).then(response => {
      if (!response.ok) throw new Error('Article search unavailable');
      return response.json();
    }).then(payload => {
      const entries = payload && payload.entries;
      if (!entries || records.some(record => typeof entries[record.id] !== 'string')) {
        throw new Error('Incomplete article search');
      }
      // Validate the complete response before changing any searchable record.
      const text = records.map(record => normalize(entries[record.id]));
      records.forEach((record, index) => { record.text = text[index]; });
      fullTextState = 'ready';
      return true;
    }).catch(() => {
      fullTextState = 'unavailable';
      return false;
    }).finally(() => {
      clearTimeout(timeout);
      // Read current state, never the query that started the asynchronous load.
      if (tokenize(state.q).length) render();
    });
    return fullTextPromise;
  }

  function renderSearch() {
    if (tokenize(state.q).length && fullTextState === 'idle') ensureFullText();
    render();
  }

  const relevance = (record, tokens, phrase) => {
    let score = record.normalizedTitle.includes(phrase) ? 100 : 0;
    for (const token of tokens) {
      score += record.normalizedTitle.includes(token) ? 12
        : record.normalizedExcerpt.includes(token) ? 4 : 1;
    }
    return score;
  };

  function render() {
    const tokens = tokenize(state.q);
    const pending = tokens.length > 0 && fullTextState === 'loading';
    const phrase = normalize(state.q);
    const matches = records.filter(record =>
      (state.topic === 'all' || record.topics.includes(state.topic)) &&
      tokens.every(token => record.text.includes(token)));
    matches.sort((a, b) => {
      if (state.sort === 'newest') return b.date - a.date || a.order - b.order;
      if (state.sort === 'az') return collator.compare(a.title, b.title) || a.order - b.order;
      return (tokens.length ? relevance(b, tokens, phrase) - relevance(a, tokens, phrase) : 0)
        || a.order - b.order;
    });
    const matchedIds = new Set(matches.map(record => record.id));
    const fragment = document.createDocumentFragment();
    matches.forEach((record, index) => {
      record.card.hidden = index >= state.limit;
      fragment.appendChild(record.card);
    });
    records.filter(record => !matchedIds.has(record.id)).forEach(record => {
      record.card.hidden = true;
      fragment.appendChild(record.card);
    });
    grid.appendChild(fragment);
    const count = matches.length;
    const shown = Math.min(state.limit, count);
    const template = state.q ? (count === 1 ? ui.oneQueryResult : ui.queryResults)
      : count === 1 ? ui.oneResult : ui.results;
    status.textContent = pending ? ui.searching : format(template, { count, shown, query: state.q });
    grid.setAttribute('aria-busy', String(pending));
    if (showing) {
      showing.textContent = format(ui.showing, { count, shown });
      showing.hidden = pending || count === 0;
    }
    if (empty) empty.hidden = pending || count !== 0;
    if (more) more.hidden = pending || shown >= count;
    if (clear) clear.hidden = !input.value;
    if (sort) sort.value = state.sort;
    topicButtons.forEach(button => button.setAttribute('aria-pressed',
      String(button.dataset.journalTopic === state.topic)));
    root.querySelectorAll('[data-journal-reset-active]').forEach(button => {
      button.hidden = !state.q && state.topic === 'all' && state.sort === 'recommended';
    });
    lastMatches = matches;
  }

  function updateUrl(mode = 'replace') {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries({ q: state.q,
      topic: state.topic === 'all' ? '' : state.topic,
      sort: state.sort === 'recommended' ? '' : state.sort })) {
      if (value) url.searchParams.set(key, value);
      else url.searchParams.delete(key);
    }
    if (url.href === window.location.href) return;
    try {
      window.history[mode === 'push' ? 'pushState' : 'replaceState'](
        window.history.state, '', url.href);
    } catch { /* Search remains functional when a host disallows history changes. */ }
  }

  function restoreUrl() {
    clearTimeout(timer);
    const params = new URL(window.location.href).searchParams;
    state = {
      q: queryValue(params.get('q')),
      topic: allowedTopics.has(params.get('topic')) ? params.get('topic') : 'all',
      sort: allowedSorts.has(params.get('sort')) ? params.get('sort') : 'recommended',
      limit: pageSize,
    };
    input.value = state.q;
    renderSearch();
  }

  const behavior = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto' : 'smooth';
  function apply(mode = 'replace', scroll = false) {
    clearTimeout(timer);
    state.q = queryValue(input.value);
    state.limit = pageSize;
    renderSearch();
    updateUrl(mode);
    if (scroll && window.matchMedia('(max-width: 720px)').matches) {
      input.blur();
      (root.querySelector('#articles') || status).scrollIntoView({ block: 'start', behavior: behavior() });
    }
  }

  input.addEventListener('input', () => {
    clearTimeout(timer);
    // Keep asynchronous completion aligned with the latest input, even while
    // the small rendering debounce is still pending.
    state.q = queryValue(input.value);
    state.limit = pageSize;
    if (clear) clear.hidden = !input.value;
    timer = setTimeout(() => apply('replace'), 120);
  });
  input.addEventListener('focus', ensureFullText, { once: true });
  input.addEventListener('search', () => apply('replace'));
  form.addEventListener('submit', event => {
    event.preventDefault();
    apply('push', true);
  });
  if (clear) clear.addEventListener('click', () => {
    input.value = '';
    apply('push');
    input.focus({ preventScroll: true });
  });
  root.querySelectorAll('[data-journal-query]').forEach(button => {
    button.addEventListener('click', () => {
      input.value = queryValue(button.dataset.journalQuery);
      state.topic = 'all';
      state.sort = 'recommended';
      apply('push', true);
    });
  });
  topicButtons.forEach(button => button.addEventListener('click', () => {
    state.topic = button.dataset.journalTopic;
    apply('push');
  }));
  if (sort) sort.addEventListener('change', () => {
    state.sort = allowedSorts.has(sort.value) ? sort.value : 'recommended';
    apply('push');
  });
  root.querySelectorAll('[data-journal-reset]').forEach(button => {
    button.addEventListener('click', () => {
      input.value = '';
      state.topic = 'all';
      state.sort = 'recommended';
      apply('push');
      input.focus({ preventScroll: true });
    });
  });
  if (more) more.addEventListener('click', () => {
    clearTimeout(timer);
    const currentQuery = queryValue(input.value);
    const queryChanged = currentQuery !== state.q;
    const previousShown = queryChanged ? 0 : Math.min(state.limit, lastMatches.length);
    state.q = currentQuery;
    state.limit = queryChanged ? pageSize : state.limit + pageSize;
    renderSearch();
    updateUrl('replace');
    const nextCard = lastMatches[previousShown]?.card;
    const link = nextCard?.querySelector('h3 a') || nextCard?.querySelector('h2 a')
      || nextCard?.querySelector('a[href]');
    if (link) {
      link.focus({ preventScroll: true });
      link.scrollIntoView({ block: 'nearest', behavior: behavior() });
    }
  });
  window.addEventListener('popstate', restoreUrl);

  restoreUrl();
  updateUrl('replace');
  form.hidden = false;
  root.querySelectorAll('[data-journal-topics], [data-journal-sort-control], [data-journal-popular]')
    .forEach(element => { element.hidden = false; });
  root.classList.add('journal-search-ready');
})();
